// import React from 'react';
import useWindowSize from '../hooks/useWindowSize';

const MobileOptimizedButton = ({
  children,
  type = 'button',
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  fullWidth = false,
  onClick,
  className = '',
  style = {},
  icon,
  iconPosition = 'left',
  ...props
}) => {
  const { isMobile } = useWindowSize();

  // Variantes de color
  const variants = {
    primary: {
      backgroundColor: '#4352A9',
      color: '#ffffff',
      border: '2px solid #4352A9',
      hoverBackgroundColor: '#061156',
      hoverBorderColor: '#061156',
      activeBackgroundColor: '#2a3875',
    },
    secondary: {
      backgroundColor: 'transparent',
      color: '#4352A9',
      border: '2px solid #4352A9',
      hoverBackgroundColor: '#4352A9',
      hoverColor: '#ffffff',
      activeBackgroundColor: '#061156',
    },
    success: {
      backgroundColor: '#28a745',
      color: '#ffffff',
      border: '2px solid #28a745',
      hoverBackgroundColor: '#218838',
      hoverBorderColor: '#218838',
      activeBackgroundColor: '#1e7e34',
    },
    danger: {
      backgroundColor: '#dc3545',
      color: '#ffffff',
      border: '2px solid #dc3545',
      hoverBackgroundColor: '#c82333',
      hoverBorderColor: '#c82333',
      activeBackgroundColor: '#bd2130',
    },
    warning: {
      backgroundColor: '#ffc107',
      color: '#212529',
      border: '2px solid #ffc107',
      hoverBackgroundColor: '#e0a800',
      hoverBorderColor: '#e0a800',
      activeBackgroundColor: '#d39e00',
    },
  };

  // Tamaños
  const sizes = {
    small: {
      padding: isMobile ? '12px 20px' : '8px 16px',
      fontSize: isMobile ? '14px' : '12px',
      minHeight: isMobile ? '44px' : '36px',
    },
    medium: {
      padding: isMobile ? '16px 24px' : '12px 20px',
      fontSize: isMobile ? '16px' : '14px',
      minHeight: isMobile ? '48px' : '40px',
    },
    large: {
      padding: isMobile ? '20px 32px' : '16px 28px',
      fontSize: isMobile ? '18px' : '16px',
      minHeight: isMobile ? '56px' : '48px',
    },
  };

  const currentVariant = variants[variant] || variants.primary;
  const currentSize = sizes[size] || sizes.medium;

  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontFamily: 'inherit',
    fontWeight: '600',
    textAlign: 'center',
    textDecoration: 'none',
    borderRadius: '8px',
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    outline: 'none',
    userSelect: 'none',
    position: 'relative',
    overflow: 'hidden',
    width: fullWidth ? '100%' : 'auto',
    opacity: disabled ? 0.6 : 1,
    ...currentSize,
    backgroundColor: currentVariant.backgroundColor,
    color: currentVariant.color,
    border: currentVariant.border,
    ...style,
  };

  const handleClick = (e) => {
    if (disabled || loading) {
      e.preventDefault();
      return;
    }
    if (onClick) onClick(e);
  };

  const handleMouseEnter = (e) => {
    if (!disabled && !loading && !isMobile) {
      e.target.style.backgroundColor = currentVariant.hoverBackgroundColor;
      if (currentVariant.hoverColor) {
        e.target.style.color = currentVariant.hoverColor;
      }
      if (currentVariant.hoverBorderColor) {
        e.target.style.borderColor = currentVariant.hoverBorderColor;
      }
      e.target.style.transform = 'translateY(-2px)';
      e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
    }
  };

  const handleMouseLeave = (e) => {
    if (!disabled && !loading && !isMobile) {
      e.target.style.backgroundColor = currentVariant.backgroundColor;
      e.target.style.color = currentVariant.color;
      e.target.style.borderColor = currentVariant.border.split(' ')[2];
      e.target.style.transform = 'translateY(0)';
      e.target.style.boxShadow = 'none';
    }
  };

  const handleTouchStart = (e) => {
    if (!disabled && !loading && isMobile) {
      e.target.style.backgroundColor = currentVariant.activeBackgroundColor;
      e.target.style.transform = 'scale(0.98)';
    }
  };

  const handleTouchEnd = (e) => {
    if (!disabled && !loading && isMobile) {
      setTimeout(() => {
        e.target.style.backgroundColor = currentVariant.backgroundColor;
        e.target.style.transform = 'scale(1)';
      }, 150);
    }
  };

  return (
    <button
      type={type}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      disabled={disabled || loading}
      style={baseStyle}
      className={className}
      {...props}
    >
      {loading && (
        <div
          style={{
            width: '16px',
            height: '16px',
            border: '2px solid transparent',
            borderTop: '2px solid currentColor',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
      )}
      
      {!loading && icon && iconPosition === 'left' && (
        <span className="material-icons" style={{ fontSize: 'inherit' }}>
          {icon}
        </span>
      )}
      
      {!loading && children}
      
      {!loading && icon && iconPosition === 'right' && (
        <span className="material-icons" style={{ fontSize: 'inherit' }}>
          {icon}
        </span>
      )}
    </button>
  );
};

export default MobileOptimizedButton;
